import {Injectable} from '@angular/core';
import {Store} from '@ngxs/store';
import {TENANT_SETTINGS} from 'app/app.constants';
import {MapProviderFactory} from 'app/shared/map/services/map-provider.factory';
import {
    LatLng,
    MapOptions,
    MarkerOptions,
    PolylineOptions,
    PolylineStyleOptions,
    PolygonOptions,
    MapBounds,
    ClusterOptions,
    MapEventType,
    MapEvent,
    GeoJsonPolygon,
    IMapProvider,
    MapProviderType
} from 'app/shared/map/interfaces';
import {geoJsonToLatLng, latLngToGeoJson} from 'app/shared/map/utils/polyline-decoder.util';

/**
 * MapService
 *
 * Main service for map operations. This service wraps map providers (HERE Maps or OpenStreetMap)
 * and provides a clean, provider-agnostic API for components to use.
 *
 * The map provider is automatically selected based on the 'mapProvider' tenant setting.
 * If no setting is found, it defaults to OpenStreetMap.
 *
 * Usage:
 * ```typescript
 * constructor(private mapService: MapService) {}
 *
 * ngAfterViewInit() {
 *   this.mapService.initialize(this.mapElement.nativeElement, {
 *     center: { lat: 52.55, lng: -0.16 },
 *     zoom: 10
 *   });
 *
 *   this.mapService.addMarker({ lat: 52.5, lng: -0.1, popup: '<b>Hello</b>' });
 * }
 * ```
 */
@Injectable()
export class MapService {
    private provider: IMapProvider;
    private providerType: MapProviderType = MapProviderType.OPEN_STREET_MAP;

    constructor(private store: Store) {
        const commonState = this.store.selectSnapshot((state) => state.common);
        const tenantSettings = commonState?.[TENANT_SETTINGS];

        if (tenantSettings?.loaded && tenantSettings?.tenantSettings) {
            const mapProviderSetting = tenantSettings.tenantSettings.find((ts) => ts.key === 'mapProvider');

            if (mapProviderSetting?.value) {
                this.provider = MapProviderFactory.createFromString(mapProviderSetting.value);

                const normalizedValue = mapProviderSetting.value.toUpperCase().trim();
                if (normalizedValue === 'OPEN_STREET_MAP' || normalizedValue === 'OSM' || normalizedValue === 'OPENSTREETMAP') {
                    this.providerType = MapProviderType.OPEN_STREET_MAP;
                } else {
                    this.providerType = MapProviderType.HERE_MAPS;
                }
            } else {
                this.provider = MapProviderFactory.create(MapProviderType.OPEN_STREET_MAP);
                this.providerType = MapProviderType.OPEN_STREET_MAP;
            }
        } else {
            this.provider = MapProviderFactory.create(MapProviderType.OPEN_STREET_MAP);
            this.providerType = MapProviderType.OPEN_STREET_MAP;
        }
    }

    /**
     * Set the map provider type (e.g., from tenant settings)
     * This should be called BEFORE initialize() to take effect
     * @param providerString - Provider type as string (e.g., 'HERE_MAPS' or 'OPEN_STREET_MAP')
     */
    setProvider(providerString: string): void {
        // Destroy existing provider if map is already initialized
        if (this.provider) {
            try {
                this.provider.destroy();
            } catch (e) {
                // Ignore errors if provider was not initialized
            }
        }

        // Create new provider
        this.provider = MapProviderFactory.createFromString(providerString);

        // Store provider type for reference
        const normalizedString = providerString?.toUpperCase().trim();
        if (normalizedString === 'OPEN_STREET_MAP' || normalizedString === 'OSM' || normalizedString === 'OPENSTREETMAP') {
            this.providerType = MapProviderType.OPEN_STREET_MAP;
        } else {
            this.providerType = MapProviderType.HERE_MAPS;
        }
    }

    /**
     * Get the current provider type
     */
    getProviderType(): MapProviderType {
        return this.providerType;
    }

    /**
     * Initialize the map on a given HTML element
     */
    initialize(element: HTMLElement, options: MapOptions): void {
        this.provider.initialize(element, options);
    }

    /**
     * Destroy the map and cleanup resources
     */
    destroy(): void {
        this.provider.destroy();
    }

    /**
     * Get the underlying map instance (for advanced usage)
     */
    getMap(): any {
        return this.provider.getMap();
    }

    setCenter(lat: number, lng: number): void {
        this.provider.setCenter(lat, lng);
    }

    setZoom(zoom: number): void {
        this.provider.setZoom(zoom);
    }

    getCenter(): LatLng | null {
        return this.provider.getCenter();
    }

    getZoom(): number | null {
        return this.provider.getZoom();
    }

    setView(lat: number, lng: number, zoom: number): void {
        this.provider.setView(lat, lng, zoom);
    }

    fitBounds(bounds: MapBounds, padding?: number): void {
        this.provider.fitBounds(bounds, padding);
    }

    fitBoundsToMarkers(padding?: number): void {
        this.provider.fitBoundsToMarkers(padding);
    }

    fitBoundsToGroup(groupId: string, padding?: number): void {
        this.provider.fitBoundsToGroup(groupId, padding);
    }

    panTo(lat: number, lng: number): void {
        this.provider.panTo(lat, lng);
    }

    /**
     * Invalidate map size (call when container size changes)
     */
    invalidateSize(): void {
        this.provider.invalidateSize();
    }

    /**
     * Add a marker to the map
     * @returns Marker ID for later reference
     */
    addMarker(options: MarkerOptions): string {
        return this.provider.addMarker(options);
    }

    /**
     * Remove a marker by ID
     */
    removeMarker(id: string): void {
        this.provider.removeMarker(id);
    }

    /**
     * Update marker position
     */
    updateMarkerPosition(id: string, lat: number, lng: number): void {
        this.provider.updateMarkerPosition(id, lat, lng);
    }

    /**
     * Get marker position
     */
    getMarkerPosition(id: string): LatLng {
        return this.provider.getMarkerPosition(id);
    }

    /**
     * Update marker icon
     */
    updateMarkerIcon(id: string, iconHtml: string): void {
        this.provider.updateMarkerIcon(id, iconHtml);
    }

    /**
     * Update marker popup content
     */
    updateMarkerPopup(id: string, popupContent: string): void {
        this.provider.updateMarkerPopup(id, popupContent);
    }

    /**
     * Clear all markers from the map
     */
    clearMarkers(): void {
        this.provider.clearMarkers();
    }

    /**
     * Get custom data stored on a marker
     */
    getMarkerData(id: string): any {
        return this.provider.getMarkerData(id);
    }

    /**
     * Open a marker's popup
     */
    openMarkerPopup(id: string): void {
        this.provider.openMarkerPopup(id);
    }

    /**
     * Close a marker's popup
     */
    closeMarkerPopup(id: string): void {
        this.provider.closeMarkerPopup(id);
    }

    /**
     * Enable marker clustering
     */
    enableClustering(options?: ClusterOptions): void {
        this.provider.enableClustering(options);
    }

    /**
     * Disable marker clustering
     */
    disableClustering(): void {
        this.provider.disableClustering();
    }

    /**
     * Rebuild cluster layer after batch marker additions
     * This is needed for HERE Maps which works better with batch data
     * For Leaflet/OSM, this is a no-op
     */
    rebuildClusterLayer(): void {
        if (this.provider.rebuildClusterLayer) {
            this.provider.rebuildClusterLayer();
        }
    }

    /**
     * Add a polyline from coordinates
     * @returns Polyline ID for later reference
     */
    addPolyline(options: PolylineOptions): string {
        return this.provider.addPolyline(options);
    }

    /**
     * Add a polyline from encoded string (GraphHopper format)
     * @param encoded - Single encoded polyline string
     * @returns Polyline ID
     */
    addPolylineFromEncoded(encoded: string, options?: PolylineStyleOptions): string {
        return this.provider.addPolylineFromEncoded(encoded, options);
    }

    /**
     * Add multiple polyline segments from comma-separated encoded strings
     * Used for leg routes: "polyline1,polyline2,polyline3"
     * @param encodedSegments - Comma-separated encoded polyline strings
     * @returns Polyline ID (references all segments)
     */
    addMultiPolylineFromEncoded(encodedSegments: string, options?: PolylineStyleOptions): string {
        return this.provider.addMultiPolylineFromEncoded(encodedSegments, options);
    }

    /**
     * Remove a polyline by ID
     */
    removePolyline(id: string): void {
        this.provider.removePolyline(id);
    }

    /**
     * Clear all polylines from the map
     */
    clearPolylines(): void {
        this.provider.clearPolylines();
    }

    /**
     * Fit map bounds to a polyline
     */
    fitBoundsToPolyline(id: string, padding?: number): void {
        this.provider.fitBoundsToPolyline(id, padding);
    }

    /**
     * Add a polygon from coordinates
     * @returns Polygon ID for later reference
     */
    addPolygon(coordinates: LatLng[], options?: PolygonOptions): string {
        return this.provider.addPolygon(coordinates, options);
    }

    /**
     * Add a polygon from GeoJSON format (as stored in backend)
     * @param geoJson - GeoJSON polygon object: { type: "Polygon", coordinates: [[[lat, lng], ...]] }
     * @returns Polygon ID
     */
    addPolygonFromGeoJson(geoJson: GeoJsonPolygon, options?: PolygonOptions): string {
        const coordinates = geoJsonToLatLng(geoJson.coordinates);
        return this.provider.addPolygon(coordinates, options);
    }

    /**
     * Remove a polygon by ID
     */
    removePolygon(id: string): void {
        this.provider.removePolygon(id);
    }

    /**
     * Clear all polygons from the map
     */
    clearPolygons(): void {
        this.provider.clearPolygons();
    }

    /**
     * Get polygon coordinates
     * @returns Array of LatLng or null if not found
     */
    getPolygonCoordinates(id: string): LatLng[] | null {
        return this.provider.getPolygonCoordinates(id);
    }

    /**
     * Get polygon coordinates in GeoJSON format for saving to backend
     */
    getPolygonAsGeoJson(id: string): GeoJsonPolygon | null {
        const coordinates = this.provider.getPolygonCoordinates(id);
        if (!coordinates) return null;
        return latLngToGeoJson(coordinates);
    }

    /**
     * Fit map bounds to a polygon
     */
    fitBoundsToPolygon(id: string, padding?: number): void {
        this.provider.fitBoundsToPolygon(id, padding);
    }

    /**
     * Enable point movement (dragging) mode
     * @param pointId - Identifier of point to be moved
     */
    enablePointMovement(pointId: string): void {
        this.provider.enablePointMovement(pointId);
    }

    /**
     * Enable polygon drawing mode
     * @param polygonId - Identifier of polygon to be edited, or null for new polygon
     * @param onComplete - Callback when a polygon is drawn
     * @param onEdit - Optional callback when a polygon is edited
     */
    enablePolygonDrawing(
        polygonId: string,
        onComplete: (coordinates: LatLng[], polygonId?: string) => void,
        onEdit?: (coordinates: LatLng[]) => void
    ): void {
        this.provider.enablePolygonDrawing(polygonId, onComplete, onEdit);
    }

    /**
     * Complete polygon drawing mode
     */
    completePolygonDrawing(): void {
        this.provider.completePolygonDrawing();
    }

    /**
     * Disable polygon drawing mode
     */
    disablePolygonDrawing(): void {
        this.provider.disablePolygonDrawing();
    }

    /**
     * Show a popup at a location
     */
    showPopup(lat: number, lng: number, content: string | HTMLElement): void {
        this.provider.showPopup(lat, lng, content);
    }

    /**
     * Close all open popups
     */
    closeAllPopups(): void {
        this.provider.closeAllPopups();
    }

    /**
     * Check if any popup is currently open (works for both Leaflet and HERE Maps)
     */
    isAnyPopupOpen(): boolean {
        return this.provider.isAnyPopupOpen();
    }

    /**
     * Create a layer group for organizing map objects
     */
    createGroup(id: string): void {
        this.provider.createGroup(id);
    }

    /**
     * Add an object to a group
     */
    addToGroup(groupId: string, objectId: string): void {
        this.provider.addToGroup(groupId, objectId);
    }

    /**
     * Clear all objects from a group
     */
    clearGroup(groupId: string): void {
        this.provider.clearGroup(groupId);
    }

    /**
     * Remove a group from the map
     */
    removeGroup(groupId: string): void {
        this.provider.removeGroup(groupId);
    }

    /**
     * Get the bounding box of a group
     */
    getGroupBounds(groupId: string): MapBounds | null {
        return this.provider.getGroupBounds(groupId);
    }

    /**
     * Register an event handler
     */
    on(event: MapEventType, handler: (e: MapEvent) => void): void {
        this.provider.on(event, handler);
    }

    /**
     * Unregister an event handler
     */
    off(event: MapEventType, handler?: (e: MapEvent) => void): void {
        this.provider.off(event, handler);
    }

    /**
     * Show or hide traffic flow overlay (Leaflet/OSM only)
     */
    setTrafficFlowVisible(visible: boolean): void {
        if (this.provider.setTrafficFlowVisible) {
            this.provider.setTrafficFlowVisible(visible);
        }
    }

    /**
     * Show or hide traffic incidents overlay (Leaflet/OSM only)
     */
    setTrafficIncidentsVisible(visible: boolean): void {
        if (this.provider.setTrafficIncidentsVisible) {
            this.provider.setTrafficIncidentsVisible(visible);
        }
    }

    /**
     * Remove all objects from the map (markers, polylines, polygons)
     */
    clearAll(): void {
        this.provider.clearAll();
    }
}
